import React, { useCallback } from 'react';

import { TextField } from '@mui/material';

import type { SeasonGridKey, Weekday } from './types';

interface Props {
  /** Sell is never blank once typed, so 0 renders empty; a cost of 0 is meaningful and renders as 0. */
  kind: 'sell' | 'cost';
  onChange: (seasonKey: SeasonGridKey, weekday: Weekday, value: number | null) => void;
  placeholder?: string;
  seasonKey: SeasonGridKey;
  value: number | null;
  weekday: Weekday;
}

export default function SurchargeAmountCell({ kind, onChange, placeholder, seasonKey, value, weekday }: Props) {
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const raw = event.target.value;
      if (raw === '') {
        onChange(seasonKey, weekday, kind === 'sell' ? 0 : null);
        return;
      }

      // `min: 0` is browser validation only, so a typed negative would otherwise reach the engine.
      const parsed = Number(raw);
      if (Number.isFinite(parsed) && parsed >= 0) {
        onChange(seasonKey, weekday, parsed);
      }
    },
    [kind, onChange, seasonKey, weekday],
  );

  const displayed = value == null || (kind === 'sell' && value === 0) ? '' : String(value);

  return (
    <TextField
      inputProps={{ min: 0, step: 0.01, style: { textAlign: 'center' } }}
      onChange={handleChange}
      placeholder={placeholder}
      size="small"
      sx={{
        // 21 weekday columns share one row, so cells are sized to the mockup's density.
        minWidth: 56,
        width: '100%',
        '& .MuiInputBase-input': { fontSize: 13, px: 0.75, py: 0.5 },
        '& input[type=number]': { MozAppearance: 'textfield' },
        '& input::-webkit-inner-spin-button, & input::-webkit-outer-spin-button': {
          WebkitAppearance: 'none',
          margin: 0,
        },
      }}
      type="number"
      value={displayed}
    />
  );
}
